import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  UserGroupIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XCircleIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserCircleIcon,
  LockClosedIcon,
  LockOpenIcon,
  LinkIcon,
  UsersIcon,
  AcademicCapIcon,
  UserIcon,
  HomeIcon,
  CloudArrowUpIcon,
  DocumentArrowDownIcon,
  UserPlusIcon,
  ArrowDownTrayIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import PhoneNumberInput from '@/components/ui/PhoneNumberInput'
import { useAuth } from '@/hooks/useAuth'
import { useUserRole } from '@/hooks/useUserRole'
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  getUserStats,
  lookupUserByContact,
  enrolStudent,
  type User as ApiUser,
  type CreateUserRequest,
} from '@/api/usersApi'
import { getRoles } from '@/api/rolesApi'
import { getGrades, getStreams, type Grade, type Stream } from '@/api/academicApi'
import toast from 'react-hot-toast'

// Form schema for adding new parent
const addParentSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Valid email address is required'),
  phone: z.string().optional(),
  countryCode: z.string().optional(),
  address: z.string().optional(),
  occupation: z.string().optional(),
  emergencyContact: z.string().optional(),
  emergencyPhone: z.string().optional(),
  relationshipToStudent: z.string().optional(), // Make relationship optional
  studentEmails: z.string().optional(), // Comma-separated student emails
  roleId: z.string().min(1, 'Role is required'),
})

type AddParentData = z.infer<typeof addParentSchema>

// Edit parent schema
const editParentSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Valid email address is required'),
  phone: z.string().optional(),
  countryCode: z.string().optional(),
})

type EditParentData = z.infer<typeof editParentSchema>

// CSV upload interfaces
interface BulkUploadResult {
  success: number
  failed: number
  total: number
  errors: Array<{ row: number; email: string; error: string }>
}

interface CSVRow {
  studentFirstName?: string
  studentLastName?: string
  studentEmail?: string
  studentPhone?: string
  studentGrade?: string
  studentStream?: string
  studentDateOfBirth?: string
  studentId?: string
  parentFirstName?: string
  parentLastName?: string
  parentEmail?: string
  parentPhone?: string
  parentOccupation?: string
  relationshipToStudent?: string
  parentAddress?: string
  emergencyContact?: string
  emergencyPhone?: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  grade?: string
  dateOfBirth?: string
  occupation?: string
  relationship?: string
  address?: string
  _createdStudentId?: string
}

// Form schemas for two-step wizard
const studentFormSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().optional(),
  phone: z.string().optional(),
  gradeId: z.string().min(1, 'Grade is required'),
  streamId: z.string().min(1, 'Stream is required'),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  studentId: z.string().optional(),
})

const parentFormSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Valid email address is required'),
  phone: z.string().min(10, 'Valid phone number is required'),
  occupation: z.string().optional(),
  relationship: z.string().min(2 , 'Relationship is required'),
  address: z.string().optional(),
  emergencyContact: z.string().optional(),
  emergencyPhone: z.string().optional(),
})

type StudentFormData = z.infer<typeof studentFormSchema>
type ParentFormData = z.infer<typeof parentFormSchema>

const ParentsPage: React.FC = () => {
  const { user } = useAuth()
  const { userRole } = useUserRole()
  const [parents, setParents] = useState<ApiUser[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingParent, setEditingParent] = useState<ApiUser | null>(null)
  const [selectedParent, setSelectedParent] = useState<ApiUser | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table')
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false)
  const [childrenFilter, setChildrenFilter] = useState<string>('all')
  const [gradeFilter, setGradeFilter] = useState<string>('all')
  const [childNameSearch, setChildNameSearch] = useState('')

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalParents, setTotalParents] = useState(0)
  const [pageSize] = useState(12) // Show more parents per page

  // Sorting state
  const [sortBy, setSortBy] = useState<'createdAt' | 'firstName' | 'lastName' | 'email' | 'lastLogin'>('createdAt')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  // Available parent roles and students for linking
  const [parentRoles, setParentRoles] = useState<{ id: string, name: string }[]>([])
  const [availableStudents, setAvailableStudents] = useState<ApiUser[]>([])

  // Stats state
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, byRole: {} as Record<string, number> })

  // Add Student & Parent wizard state
  const [showFormWizard, setShowFormWizard] = useState(false)
  const [currentStep, setCurrentStep] = useState(1)
  const [studentData, setStudentData] = useState<StudentFormData | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // CSV upload state
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [csvData, setCsvData] = useState<CSVRow[]>([])
  const [showPreview, setShowPreview] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadResult, setUploadResult] = useState<BulkUploadResult | null>(null)

  // Academic data for wizard
  const [grades, setGrades] = useState<Grade[]>([])
  const [streams, setStreams] = useState<Stream[]>([])
  const [selectedGrade, setSelectedGrade] = useState<string>('')
  const [gradeSearchQuery, setGradeSearchQuery] = useState('')
  const [streamSearchQuery, setStreamSearchQuery] = useState('')
  const [showGradeDropdown, setShowGradeDropdown] = useState(false)
  const [showStreamDropdown, setShowStreamDropdown] = useState(false)
  const [availableRoles, setAvailableRoles] = useState<{ id: string, name: string, type: string }[]>([])

  const addParentForm = useForm<AddParentData>({
    resolver: zodResolver(addParentSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      countryCode: 'KE',
      address: '',
      occupation: '',
      emergencyContact: '',
      emergencyPhone: '',
      relationshipToStudent: '',
      studentEmails: '',
      roleId: '',
    }
  })

  const editParentForm = useForm<EditParentData>({
    resolver: zodResolver(editParentSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      countryCode: 'KE',
    }
  })

  // Wizard forms
  const studentForm = useForm<StudentFormData>({
    resolver: zodResolver(studentFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      gradeId: '',
      streamId: '',
      dateOfBirth: '',
      studentId: '',
    }
  })

  const parentWizardForm = useForm<ParentFormData>({
    resolver: zodResolver(parentFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      occupation: '',
      relationship: '',
      address: '',
      emergencyContact: '',
      emergencyPhone: '',
    }
  })

  const fetchParents = async () => {
    setLoading(true)
    try {
      // Get all available roles first to identify parent role IDs
      const rolesResponse = await getRoles()
      const parentRoleIds = rolesResponse.data
        .filter(role =>
          role.name.toLowerCase().includes('parent') ||
          role.name.toLowerCase().includes('guardian') ||
          role.name.toLowerCase().includes('family')
        )
        .map(role => role.id)

      if (parentRoleIds.length === 0) {
        console.warn('No parent roles found')
        setParents([])
        setLoading(false)
        return
      }

      // Use the first parent role ID found
      const primaryParentRoleId = parentRoleIds[0]

      // Fetch parents with proper pagination
      const response = await getUsers({
        page: currentPage,
        limit: pageSize,
        search: searchQuery || undefined,
        roleId: primaryParentRoleId,
        status: statusFilter !== 'all' ? statusFilter as 'active' | 'inactive' : undefined,
        sortBy,
        sortOrder,
      })

      setParents(response.users)

      // Update pagination info from API response
      if (response.pagination) {
        setCurrentPage(response.pagination.currentPage)
        setTotalPages(response.pagination.totalPages)
        setTotalParents(response.pagination.totalUsers)
      }

    } catch (error) {
      console.error('Failed to fetch parents:', error)
      toast.error('Failed to load parents')
    } finally {
      setLoading(false)
    }
  }

  const fetchParentRoles = async () => {
    try {
      const response = await getRoles()
      // Filter only parent-related roles
      const parentRolesList = response.data.filter(role =>
        role.name.toLowerCase().includes('parent') ||
        role.name.toLowerCase().includes('guardian') ||
        role.name.toLowerCase().includes('family')
      )
      setParentRoles(parentRolesList.map(role => ({ id: role.id, name: role.name })))
    } catch (error) {
      console.error('Failed to fetch parent roles:', error)
    }
  }

  const fetchAvailableStudents = async () => {
    try {
      // Get students for parent-student linking
      const rolesResponse = await getRoles()
      const studentRoleIds = rolesResponse.data
        .filter(role => role.name.toLowerCase().includes('student'))
        .map(role => role.id)

      if (studentRoleIds.length > 0) {
        const response = await getUsers({
          roleId: studentRoleIds[0],
          limit: 50, // Get more students for selection
          status: 'active'
        })
        setAvailableStudents(response.users)
      }
    } catch (error) {
      console.error('Failed to fetch students:', error)
    }
  }

  const fetchParentStats = async () => {
    try {
      const response = await getUserStats()

      // Filter stats to only include parent-related counts
      const parentStats = Object.entries(response).reduce((acc, [key, value]) => {
        if (key.toLowerCase().includes('parent') ||
            key.toLowerCase().includes('guardian') ||
            key.toLowerCase().includes('family')) {
          acc.byRole[key] = value as number
        }
        return acc
      }, { total: 0, active: 0, inactive: 0, byRole: {} as Record<string, number> })

      // Calculate totals from parent-specific data
      const parentTotal = Object.values(parentStats.byRole).reduce((sum, count) => sum + count, 0)

      setStats({
        total: totalParents || parentTotal,
        active: parents.filter(p => p.status === 'Active').length,
        inactive: parents.filter(p => p.status === 'Inactive').length,
        byRole: parentStats.byRole
      })
    } catch (error) {
      console.error('Failed to fetch parent stats:', error)
    }
  }

  useEffect(() => {
    fetchParents()
    fetchParentRoles()
    fetchAvailableStudents()
  }, [currentPage, searchQuery, statusFilter, sortBy, sortOrder, childrenFilter, gradeFilter, childNameSearch])

  useEffect(() => {
    if (parents.length >= 0) {
      fetchParentStats()
    }
  }, [parents])

  // Debounced search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1) // Reset to first page when filters change
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [searchQuery, statusFilter])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const getRoleBadge = (user: ApiUser) => {
    const roleName = user.role || 'Parent'
    return (
      <span className="badge badge-purple">
        {roleName}
      </span>
    )
  }

  const getStatusBadge = (user: ApiUser) => {
    const isActive = user.status === "Active"
    return (
      <span className={`badge ${isActive ? 'badge-success' : 'badge-gray'}`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    )
  }

  const handleViewParent = (parent: ApiUser) => {
    setSelectedParent(parent)
  }

  const handleEditParent = (parent: ApiUser) => {
    setEditingParent(parent)
    editParentForm.reset({
      firstName: parent.firstName || '',
      lastName: parent.lastName || '',
      email: parent.email || '',
      phone: parent.phone || '',
      countryCode: parent.countryCode || 'KE',
    })
    setShowEditModal(true)
  }

  const handleSaveEdit = async (data: EditParentData) => {
    if (!editingParent) return

    try {
      await updateUser(editingParent.id, {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone || undefined,
        countryCode: data.countryCode || undefined,
      })
      toast.success(`${data.firstName} ${data.lastName} updated successfully`)
      setShowEditModal(false)
      setEditingParent(null)
      editParentForm.reset()
      fetchParents()
    } catch (error: any) {
      toast.error(error.message || 'Failed to update parent')
    }
  }

  const handleDeleteParent = async (parent: ApiUser) => {
    if (confirm(`Are you sure you want to remove ${parent.firstName} ${parent.lastName} from parents?`)) {
      try {
        await deleteUser(parent.id)
        toast.success(`${parent.firstName} ${parent.lastName} removed successfully`)
        fetchParents()
      } catch (error) {
        toast.error('Failed to remove parent')
      }
    }
  }

  const handleToggleStatus = async (parent: ApiUser) => {
    try {
      const newIsActive = parent.status === 'Active' ? false : true
      await updateUser(parent.id, { isActive: newIsActive })
      toast.success(`${parent.firstName} ${parent.lastName} ${newIsActive ? 'activated' : 'deactivated'}`)
      fetchParents()
    } catch (error) {
      toast.error('Failed to update parent status')
    }
  }

  const handleLinkToStudent = (parent: ApiUser) => {
    setSelectedParent(parent)
    setShowLinkModal(true)
  }

  const handleAddParent = async (parentData: AddParentData) => {
    try {
      // Lookup student IDs from emails if provided
      let childrenIds: string[] = []

      if (parentData.studentEmails && parentData.studentEmails.trim()) {
        const emails = parentData.studentEmails
          .split(',')
          .map(email => email.trim())
          .filter(email => email.length > 0)

        const lookupResults = await Promise.all(
          emails.map(async (email) => {
            try {
              const result = await lookupUserByContact({ email })
              if (result.success && result.data) {
                return result.data.id
              }
              return null
            } catch {
              console.warn(`Failed to lookup student with email: ${email}`)
              return null
            }
          })
        )

        // Filter out null results and collect valid IDs
        childrenIds = lookupResults.filter((id): id is string => id !== null)

        if (childrenIds.length === 0 && emails.length > 0) {
          toast.error('No students found with the provided emails')
          return
        }

        if (childrenIds.length < emails.length) {
          toast(`⚠️ ${emails.length - childrenIds.length} student email(s) not found, proceeding with ${childrenIds.length} found`)
        }
      }

      await createUser({
        firstName: parentData.firstName,
        lastName: parentData.lastName,
        email: parentData.email,
        roleId: parentData.roleId,
        phone: parentData.phone,
        countryCode: parentData.countryCode,
        childrenIds: childrenIds.length > 0 ? childrenIds : undefined,
        // institutionId will be set automatically by backend based on authenticated user
        // password will be auto-generated by backend
      })

      toast.success(`${parentData.firstName} ${parentData.lastName} registered successfully!`)
      setShowAddModal(false)
      addParentForm.reset()
      fetchParents()

    } catch (error: any) {
      console.error('Failed to add parent:', error)
      const message = error?.response?.data?.message || 'Failed to register parent. Please try again.'
      toast.error(message)
    }
  }

  // Fetch academic data for wizard
  const fetchGrades = async () => {
    try {
      const response = await getGrades()
      const gradesData = response.grades || []
      setGrades(Array.isArray(gradesData) ? gradesData : [])
    } catch (error) {
      console.error('Failed to fetch grades:', error)
      setGrades([])
    }
  }

  const fetchStreamsByGrade = async (gradeId: string) => {
    try {
      const streamsData = await getStreams({ gradeId })
      setStreams(Array.isArray(streamsData) ? streamsData : [])
    } catch (error) {
      console.error('Failed to fetch streams:', error)
      setStreams([])
    }
  }

  const fetchAllRoles = async () => {
    try {
      const response = await getRoles()
      const rolesWithType = response.data.map(role => ({
        id: role.id,
        name: role.name,
        type: role.name.toLowerCase().includes('student') ? 'student' :
              role.name.toLowerCase().includes('parent') ? 'parent' : 'other'
      }))
      setAvailableRoles(rolesWithType)
    } catch (error) {
      console.error('Failed to fetch roles:', error)
    }
  }

  // Wizard handlers
  const handleStudentFormNext = (data: StudentFormData) => {
    setStudentData(data)
    setCurrentStep(2)
  }

  const handleFormWizardSubmit = async (parentData: ParentFormData) => {
    if (!studentData) {
      toast.error('Student data is missing')
      return
    }

    setIsSubmitting(true)

    try {
      const familyPayload = {
        student: {
          firstName: studentData.firstName,
          lastName: studentData.lastName,
          email: studentData.email,
          phone: studentData.phone,
          gradeId: studentData.gradeId,
          streamId: studentData.streamId,
          studentId: studentData.studentId || undefined
        },
        parent: {
          firstName: parentData.firstName,
          lastName: parentData.lastName,
          email: parentData.email,
          phone: parentData.phone,
          occupation: parentData.occupation,
          relationship: parentData.relationship,
          address: parentData.address,
          emergencyContact: parentData.emergencyContact,
          emergencyPhone: parentData.emergencyPhone
        },
        institutionId: user?.role === 'SUPER_ADMIN' ? user?.institutionId : undefined
      }

      const response = await enrolStudent(familyPayload)

      toast.success(`Successfully created family accounts! Student and Parent linked automatically.`, {
        duration: 6000
      })

      resetWizard()
      fetchParents()

    } catch (error: any) {
      console.error('Failed to create family accounts:', error)
      toast.error(error?.response?.data?.message || 'Failed to create family accounts')
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetWizard = () => {
    setShowFormWizard(false)
    setCurrentStep(1)
    setStudentData(null)
    setSelectedGrade('')
    setGradeSearchQuery('')
    setStreamSearchQuery('')
    setStreams([])
    setShowGradeDropdown(false)
    setShowStreamDropdown(false)
    studentForm.reset()
    parentWizardForm.reset()
  }

  const handleGradeSelection = (grade: Grade) => {
    const gradeId = grade.id
    setSelectedGrade(gradeId)
    studentForm.setValue('gradeId', gradeId)
    studentForm.setValue('streamId', '')
    setGradeSearchQuery(grade.name)
    setStreamSearchQuery('')
    setShowGradeDropdown(false)
    fetchStreamsByGrade(gradeId)
  }

  const handleStreamSelection = (stream: Stream) => {
    const streamId = stream.id
    studentForm.setValue('streamId', streamId)
    setStreamSearchQuery(stream.name)
    setShowStreamDropdown(false)
  }

  // CSV upload handlers
  const parseCSV = (csvText: string): CSVRow[] => {
    const lines = csvText.split('\n').filter(line => line.trim())
    if (lines.length < 2) return []

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
    const rows = lines.slice(1).map((line) => {
      const values = line.split(',').map(v => v.trim())
      const row: any = {}

      headers.forEach((header, i) => {
        const value = values[i] || ''

        switch (header) {
          case 'studentfirstname': case 'student_first_name': case 'firstname': case 'first_name':
            row.studentFirstName = value; row.firstName = value; break
          case 'studentlastname': case 'student_last_name': case 'lastname': case 'last_name':
            row.studentLastName = value; row.lastName = value; break
          case 'studentemail': case 'student_email': case 'email':
            row.studentEmail = value; row.email = value; break
          case 'studentphone': case 'student_phone': case 'phone':
            row.studentPhone = value; row.phone = value; break
          case 'studentgrade': case 'student_grade': case 'grade': case 'class':
            row.studentGrade = value; row.grade = value; break
          case 'studentstream': case 'student_stream': case 'stream':
            row.studentStream = value; break
          case 'studentdateofbirth': case 'date_of_birth': case 'dob':
            row.studentDateOfBirth = value; row.dateOfBirth = value; break
          case 'studentid': case 'student_id':
            row.studentId = value; break
          case 'parentfirstname': case 'parent_first_name':
            row.parentFirstName = value; break
          case 'parentlastname': case 'parent_last_name':
            row.parentLastName = value; break
          case 'parentemail': case 'parent_email':
            row.parentEmail = value; break
          case 'parentphone': case 'parent_phone':
            row.parentPhone = value; break
          case 'parentoccupation': case 'occupation':
            row.parentOccupation = value; row.occupation = value; break
          case 'relationshiptostudent': case 'relationship':
            row.relationshipToStudent = value; row.relationship = value; break
          case 'parentaddress': case 'address':
            row.parentAddress = value; row.address = value; break
          case 'emergencycontact': case 'emergency_contact':
            row.emergencyContact = value; break
          case 'emergencyphone': case 'emergency_phone':
            row.emergencyPhone = value; break
          default:
            row[header] = value
        }
      })

      return row
    })

    return rows.filter(row =>
      (row.studentFirstName && row.studentLastName && row.studentEmail) ||
      (row.parentFirstName && row.parentLastName && row.parentEmail) ||
      (row.firstName && row.lastName && row.email)
    )
  }

  const handleFileSelect = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file')
      return
    }
    setSelectedFile(file)
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) handleFileSelect(file)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFileSelect(file)
  }

  const processSelectedFile = () => {
    if (!selectedFile) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const csvText = e.target?.result as string
      const parsed = parseCSV(csvText)

      if (parsed.length === 0) {
        toast.error('No valid data found in CSV file')
        return
      }

      setCsvData(parsed)
      setShowPreview(true)
      setShowUploadModal(false)
      setSelectedFile(null)
      toast.success(`Parsed ${parsed.length} records from CSV`)
    }

    reader.readAsText(selectedFile)
  }

  const closeUploadModal = () => {
    setShowUploadModal(false)
    setSelectedFile(null)
    setIsDragging(false)
  }

  const handleBulkUpload = async () => {
    if (csvData.length === 0) {
      toast.error('No data to upload')
      return
    }

    setIsUploading(true)
    setUploadProgress(0)

    const studentRoleId = availableRoles.find(role => role.type === 'student')?.id
    const parentRoleId = availableRoles.find(role => role.type === 'parent')?.id

    if (!studentRoleId || !parentRoleId) {
      toast.error('Student or Parent roles not found')
      setIsUploading(false)
      return
    }

    const results: BulkUploadResult = {
      success: 0,
      failed: 0,
      total: csvData.length * 2,
      errors: []
    }

    for (let i = 0; i < csvData.length; i++) {
      const row = csvData[i]

      try {
        if (row.studentFirstName && row.studentLastName && row.studentEmail) {
          const gradeName = row.studentGrade || row.grade || ''
          const streamName = row.studentStream || ''

          let gradeId = ''
          let streamId = ''

          if (gradeName) {
            const grade = grades.find(g => g.name.toLowerCase() === gradeName.toLowerCase())
            gradeId = grade ? grade.id : ''
          }

          if (streamName && gradeId) {
            let availableStreams = streams
            if (gradeId !== selectedGrade) {
              try {
                availableStreams = await getStreams({ gradeId })
              } catch (error) {
                console.warn('Could not fetch streams for grade:', gradeId)
              }
            }
            const stream = availableStreams.find(s => s.name.toLowerCase() === streamName.toLowerCase())
            streamId = stream ? stream.id : ''
          }

          const studentData: CreateUserRequest = {
            firstName: row.studentFirstName || row.firstName || '',
            lastName: row.studentLastName || row.lastName || '',
            email: row.studentEmail || row.email || '',
            roleId: studentRoleId,
            phone: row.studentPhone || row.phone,
            institutionId: user?.institutionId,
            gradeId: gradeId || undefined,
            streamId: streamId || undefined,
            studentId: row.studentId || undefined
          }

          const studentResponse = await createUser(studentData)
          const createdStudentId = studentResponse.data.user.id
          results.success++

          row._createdStudentId = createdStudentId
        }

        if (row.parentFirstName && row.parentLastName && row.parentEmail) {
          const parentData: CreateUserRequest = {
            firstName: row.parentFirstName,
            lastName: row.parentLastName,
            email: row.parentEmail,
            roleId: parentRoleId,
            phone: row.parentPhone,
            institutionId: user?.institutionId,
            childrenIds: row._createdStudentId ? [row._createdStudentId] : []
          }

          await createUser(parentData)
          results.success++
        }

      } catch (error: any) {
        results.failed++
        results.errors.push({
          row: i + 1,
          email: row.studentEmail || row.parentEmail || row.email || 'Unknown',
          error: error?.response?.data?.message || 'Unknown error'
        })
      }

      const progress = ((i + 1) / csvData.length) * 100
      setUploadProgress(progress)

      await new Promise(resolve => setTimeout(resolve, 150))
    }

    setUploadResult(results)
    setIsUploading(false)

    if (results.success > 0) {
      toast.success(`Successfully created ${results.success} accounts from family data`)
      fetchParents()
    }

    if (results.failed > 0) {
      toast.error(`Failed to create ${results.failed} accounts`)
    }
  }

  const downloadTemplate = () => {
    const headers = [
      'studentFirstName', 'studentLastName', 'studentEmail', 'studentPhone', 'studentGrade', 'studentStream', 'studentDateOfBirth', 'studentId',
      'parentFirstName', 'parentLastName', 'parentEmail', 'parentPhone', 'parentOccupation', 'relationshipToStudent', 'parentAddress', 'emergencyContact', 'emergencyPhone'
    ]
    const sampleData = [
      'John,Doe,john.doe@students.school.edu,+254712345678,Grade 5,Stream A,2010-05-15,STU24001,Mary,Doe,mary.doe@parent.co.ke,+254734567890,Engineer,Mother,123 Oak Street Nairobi,James Doe,+254700345678',
      'Jane,Smith,jane.smith@students.school.edu,+254723456789,Grade 6,Stream B,2009-08-22,STU24002,Peter,Smith,peter.smith@parent.co.ke,+254745678901,Teacher,Father,456 Pine Avenue Nairobi,Grace Smith,+254700456789'
    ]

    const csvContent = [headers.join(','), ...sampleData].join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'families_template.csv'
    a.click()
    window.URL.revokeObjectURL(url)

    toast.success('Family template downloaded')
  }

  const resetUpload = () => {
    setCsvData([])
    setShowPreview(false)
    setUploadResult(null)
    setUploadProgress(0)
  }

  // Load academic data when wizard opens
  useEffect(() => {
    if (showFormWizard) {
      fetchGrades()
      fetchAllRoles()
    }
  }, [showFormWizard])

  useEffect(() => {
    if (showUploadModal || showPreview) {
      fetchGrades()
      fetchAllRoles()
    }
  }, [showUploadModal, showPreview])

  useEffect(() => {
    if (selectedGrade) {
      fetchStreamsByGrade(selectedGrade)
    } else {
      setStreams([])
    }
  }, [selectedGrade])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <UserGroupIcon className="h-8 w-8 text-purple-600 mr-3" />
            Parents & Guardians
          </h1>
          <p className="text-gray-600 mt-1">
            {userRole === 'school_admin'
              ? 'Manage parent and guardian accounts for your institution'
              : 'Manage all parents and guardians across institutions'
            }
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFormWizard(true)}
            className="btn btn-primary"
          >
            <UserPlusIcon className="h-4 w-4 mr-2" />
            Add Student & Parent
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn btn-secondary"
          >
            <CloudArrowUpIcon className="h-4 w-4 mr-2" />
            Upload CSV
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-secondary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Register Parent Only
          </button>
        </div>
      </div>

      {/* Quick Actions Bar */}
      <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
        <div className="text-sm text-gray-600">
          Quickly onboard families with students and parents together, or upload in bulk via CSV.
        </div>
        <button
          onClick={downloadTemplate}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center"
        >
          <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
          Download CSV Template
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Parents</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total?.toLocaleString() || 0}</p>
            </div>
            <UserGroupIcon className="h-8 w-8 text-purple-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Parents</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active || 0}</p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-success-600" />
          </div>
        </div>
        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive Parents</p>
              <p className="text-2xl font-bold text-gray-900">{stats.inactive || 0}</p>
            </div>
            <XCircleIcon className="h-8 w-8 text-red-500" />
          </div>
        </div>

        <div className="dashboard-stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">With Children</p>
              <p className="text-2xl font-bold text-gray-900">
                {parents.filter(p => p.hasChildren).length}
              </p>
            </div>
            <UsersIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
        <div className="flex-1 relative max-w-md">
          <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search parents and guardians..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input pl-10"
          />
        </div>

        <div className="flex items-center space-x-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <select
            value={childrenFilter}
            onChange={(e) => setChildrenFilter(e.target.value)}
            className="form-input text-sm"
          >
            <option value="all">All Parents</option>
            <option value="with_children">With Children</option>
            <option value="no_children">No Children Linked</option>
          </select>

          <button
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              showAdvancedSearch
                ? 'bg-purple-50 border-purple-200 text-purple-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            Advanced Search
          </button>

          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded text-sm ${viewMode === 'table' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Table
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded text-sm ${viewMode === 'grid' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-600'}`}
            >
              Grid
            </button>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalParents)} of {totalParents} parents</span>
        <div className="flex items-center space-x-3">
          <span className="text-xs text-gray-500">
            {userRole === 'school_admin' ? 'Parents in your institution' : 'All parents'}
          </span>
        </div>
      </div>

      {/* Advanced Search Panel */}
      {showAdvancedSearch && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-purple-50 border border-purple-200 rounded-lg p-6"
        >
          <h3 className="text-lg font-semibold text-purple-900 mb-4">Advanced Parent Search</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Child Name</label>
              <input
                type="text"
                placeholder="Search by child's name..."
                value={childNameSearch}
                onChange={(e) => setChildNameSearch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Children's Grade</label>
              <select
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">All Grades</option>
                <option value="Grade 1">Grade 1</option>
                <option value="Grade 2">Grade 2</option>
                <option value="Grade 3">Grade 3</option>
                <option value="Grade 4">Grade 4</option>
                <option value="Grade 5">Grade 5</option>
                <option value="Grade 6">Grade 6</option>
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-purple-200">
            <div className="text-sm text-purple-700">
              💡 Find parents by their children's names, grades, or academic information
            </div>
            <button
              onClick={() => {
                setChildNameSearch('')
                setGradeFilter('all')
                setSearchQuery('')
                setStatusFilter('all')
                setChildrenFilter('all')
              }}
              className="px-4 py-2 text-sm text-purple-600 hover:text-purple-800"
            >
              Clear All Filters
            </button>
          </div>
        </motion.div>
      )}

      {/* Parents Table */}
      {viewMode === 'table' && (
        <div className="dashboard-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Parent/Guardian
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Children Academic Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact Info
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registered
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {parents.map((parent) => (
                  <tr key={parent.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-pink-200 rounded-full flex items-center justify-center mr-3">
                          <UserGroupIcon className="h-6 w-6 text-purple-600" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {parent.firstName} {parent.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {getRoleBadge(parent)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {parent.childrenAcademicInfo && parent.childrenAcademicInfo.length > 0 ? (
                          <>
                            {parent.childrenAcademicInfo.map((child, index) => (
                              <div key={index} className="border-l-2 border-blue-300 pl-3 mb-2">
                                <div className="flex items-center space-x-2">
                                  <UserIcon className="h-4 w-4 text-blue-600" />
                                  <span className="text-sm font-medium text-gray-900">{child.name}</span>
                                  {child.studentId && (
                                    <span className="text-xs text-blue-600 font-mono">({child.studentId})</span>
                                  )}
                                </div>
                                {child.grade && child.stream && (
                                  <div className="flex items-center space-x-2 mt-1">
                                    <AcademicCapIcon className="h-3 w-3 text-green-500" />
                                    <span className="text-xs text-gray-600">
                                      {child.grade} - {child.stream}
                                    </span>
                                  </div>
                                )}
                                {child.classTeacher && (
                                  <div className="flex items-center space-x-2 mt-1">
                                    <UserCircleIcon className="h-3 w-3 text-purple-500" />
                                    <span className="text-xs text-gray-500">
                                      Teacher: {child.classTeacher}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                            <div className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {parent.parentSummary?.totalChildren || 0} Children
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center space-x-2 text-orange-600">
                            <XCircleIcon className="h-4 w-4" />
                            <span className="text-sm">No Children Linked</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{parent.email}</span>
                        </div>
                        {parent.phone && (
                          <div className="flex items-center space-x-2">
                            <PhoneIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-600">{parent.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(parent)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(parent.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewParent(parent)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleLinkToStudent(parent)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Link to Student"
                        >
                          <LinkIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditParent(parent)}
                          className="text-gray-600 hover:text-gray-900"
                          title="Edit Parent"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleToggleStatus(parent)}
                          className={parent.status === 'Active' ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}
                          title={parent.status === 'Active' ? 'Deactivate' : 'Activate'}
                        >
                          {parent.status === 'Active' ? (
                            <LockClosedIcon className="h-4 w-4" />
                          ) : (
                            <LockOpenIcon className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeleteParent(parent)}
                          className="text-red-600 hover:text-red-900"
                          title="Remove Parent"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {parents.length === 0 && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="dashboard-card"
        >
          <div className="text-center py-12">
            <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No parents found</h3>
            <p className="mt-2 text-gray-500">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : userRole === 'school_admin'
                  ? 'Get started by registering your first parent/guardian.'
                  : 'No parents found in the system.'
              }
            </p>
            {(!searchQuery && statusFilter === 'all') && (
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-4 btn btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Register First Parent
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* Add Parent Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Register New Parent/Guardian</h3>
                    <p className="text-sm text-gray-500">Add a new parent or guardian account</p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={addParentForm.handleSubmit(handleAddParent)} className="p-6 space-y-6" autoComplete="off">
                {/* Hidden fields to prevent autofill */}
                <input type="text" style={{display:'none'}} />
                <input type="password" style={{display:'none'}} />

                {/* Parent Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <UserGroupIcon className="h-5 w-5 text-purple-600 mr-2" />
                    Parent/Guardian Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...addParentForm.register('firstName')}
                      label="First Name"
                      placeholder="e.g., John"
                      autoComplete="off"
                      error={addParentForm.formState.errors.firstName?.message}
                    />

                    <Input
                      {...addParentForm.register('lastName')}
                      label="Last Name"
                      placeholder="e.g., Smith"
                      autoComplete="off"
                      error={addParentForm.formState.errors.lastName?.message}
                    />

                    <Input
                      {...addParentForm.register('email')}
                      type="email"
                      label="Email Address"
                      placeholder="e.g., john.smith@example.com"
                      autoComplete="off"
                      data-lpignore="true"
                      error={addParentForm.formState.errors.email?.message}
                    />

                    {/* Password is auto-generated by backend */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center">
                        <LockClosedIcon className="h-5 w-5 text-blue-600 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-blue-800">Secure Password</p>
                          <p className="text-xs text-blue-600">Auto-generated and sent via email</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <PhoneNumberInput
                        value={addParentForm.watch('phone') || ''}
                        countryCode={addParentForm.watch('countryCode') || 'KE'}
                        onChange={(phone, countryCode) => {
                          addParentForm.setValue('phone', phone)
                          addParentForm.setValue('countryCode', countryCode)
                          addParentForm.trigger(['phone', 'countryCode'])
                        }}
                        placeholder="Enter phone number"
                        error={addParentForm.formState.errors.phone?.message}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Relationship to Student <span className="text-gray-400 text-xs">(Optional)</span>
                      </label>
                      <select
                        {...addParentForm.register('relationshipToStudent')}
                        className="form-input"
                      >
                        <option value="">Select Relationship (Optional)</option>
                        <option value="Father">Father</option>
                        <option value="Mother">Mother</option>
                        <option value="Guardian">Guardian</option>
                        <option value="Grandfather">Grandfather</option>
                        <option value="Grandmother">Grandmother</option>
                        <option value="Uncle">Uncle</option>
                        <option value="Aunt">Aunt</option>
                        <option value="Other Relative">Other Relative</option>
                        <option value="Foster Parent">Foster Parent</option>
                        <option value="Legal Guardian">Legal Guardian</option>
                      </select>
                      {addParentForm.formState.errors.relationshipToStudent && (
                        <p className="mt-1 text-sm text-red-600">{addParentForm.formState.errors.relationshipToStudent.message}</p>
                      )}
                    </div>

                    <Input
                      {...addParentForm.register('occupation')}
                      label="Occupation (Optional)"
                      placeholder="e.g., Teacher, Engineer"
                      error={addParentForm.formState.errors.occupation?.message}
                    />

                    <Input
                      {...addParentForm.register('address')}
                      label="Home Address (Optional)"
                      placeholder="e.g., 123 Main Street, Nairobi"
                      error={addParentForm.formState.errors.address?.message}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Parent Role
                      </label>
                      <select
                        {...addParentForm.register('roleId')}
                        className="form-input"
                      >
                        <option value="">Select Parent Role</option>
                        {parentRoles.map(role => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                      {addParentForm.formState.errors.roleId && (
                        <p className="mt-1 text-sm text-red-600">{addParentForm.formState.errors.roleId.message}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Emergency Contact Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <PhoneIcon className="h-5 w-5 text-red-600 mr-2" />
                    Emergency Contact (Optional)
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      {...addParentForm.register('emergencyContact')}
                      label="Emergency Contact Name"
                      placeholder="e.g., Jane Doe"
                      error={addParentForm.formState.errors.emergencyContact?.message}
                    />

                    <Input
                      {...addParentForm.register('emergencyPhone')}
                      label="Emergency Contact Phone"
                      placeholder="e.g., +254 700 123 456"
                      error={addParentForm.formState.errors.emergencyPhone?.message}
                    />
                  </div>
                </div>

                {/* Student Linking Information */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center">
                    <LinkIcon className="h-5 w-5 text-blue-600 mr-2" />
                    Student Linking (Optional)
                  </h4>
                  <div className="grid grid-cols-1 gap-4">
                    <Input
                      {...addParentForm.register('studentEmails')}
                      label="Student Email Addresses"
                      placeholder="Enter student emails separated by commas (e.g., student1@school.edu, student2@school.edu)"
                      helperText="You can also link students later from the parent profile"
                      error={addParentForm.formState.errors.studentEmails?.message}
                    />
                  </div>
                </div>

                
                <div className="flex justify-end space-x-3 pt-6 border-t">
                  <Button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <UserGroupIcon className="h-4 w-4 mr-2" />
                    Register Parent
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Parent Details Modal */}
      <AnimatePresence>
        {selectedParent && !showLinkModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedParent(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-200 rounded-full flex items-center justify-center">
                      <UserGroupIcon className="h-10 w-10 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {selectedParent.firstName} {selectedParent.lastName}
                      </h3>
                      <p className="text-sm text-gray-500">{selectedParent.institution || 'No Institution'}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedParent(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Contact Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Contact Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{selectedParent.email}</span>
                      </div>
                      {selectedParent.phone && (
                        <div className="flex items-center space-x-3">
                          <PhoneIcon className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{selectedParent.phone}</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-3">
                        <HomeIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{selectedParent.institution}</span>
                      </div>
                    </div>
                  </div>

                  {/* Account Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Account Information</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Role</span>
                        <div className="flex items-center space-x-2">
                          <UserGroupIcon className="h-4 w-4 text-purple-600" />
                          {getRoleBadge(selectedParent)}
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Status</span>
                        {getStatusBadge(selectedParent)}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Registered</span>
                        <span className="text-sm text-gray-900">{formatDate(selectedParent.createdAt)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Created By</span>
                        <span className="text-sm text-gray-900">{selectedParent.createdBy || 'System'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl">
                <div className="flex justify-end space-x-3">
                  <Button
                    onClick={() => handleLinkToStudent(selectedParent)}
                    variant="secondary"
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Link to Student
                  </Button>
                  <Button
                    onClick={() => handleEditParent(selectedParent)}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <PencilIcon className="h-4 w-4 mr-2" />
                    Edit Parent
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Student Linking Modal */}
      <AnimatePresence>
        {showLinkModal && selectedParent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowLinkModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Link Parent to Students</h3>
                    <p className="text-sm text-gray-500">
                      Connect {selectedParent.firstName} {selectedParent.lastName} to their children
                    </p>
                  </div>
                  <button
                    onClick={() => setShowLinkModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Available Students</h4>
                  {availableStudents.length > 0 ? (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {availableStudents.map((student) => (
                        <label
                          key={student.id}
                          className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
                            onChange={() => toast.success('Student linking will be implemented next!')}
                          />
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-purple-200 rounded-full flex items-center justify-center">
                              <UserIcon className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {student.firstName} {student.lastName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {student.email} • {student.institution}
                              </div>
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <UserIcon className="mx-auto h-8 w-8 text-gray-400" />
                      <p className="text-sm text-gray-500 mt-2">No students available for linking</p>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                  <Button
                    onClick={() => setShowLinkModal(false)}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      toast.success('Student linking functionality coming soon!')
                      setShowLinkModal(false)
                    }}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Link Selected Students
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Parent Modal */}
      <AnimatePresence>
        {showEditModal && editingParent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowEditModal(false)
              setEditingParent(null)
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Edit Parent</h3>
                    <p className="text-sm text-gray-500">
                      Update details for {editingParent.firstName} {editingParent.lastName}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowEditModal(false)
                      setEditingParent(null)
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={editParentForm.handleSubmit(handleSaveEdit)} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      {...editParentForm.register('firstName')}
                      placeholder="e.g., Mary"
                      error={editParentForm.formState.errors.firstName?.message}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      {...editParentForm.register('lastName')}
                      placeholder="e.g., Doe"
                      error={editParentForm.formState.errors.lastName?.message}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    {...editParentForm.register('email')}
                    type="email"
                    placeholder="e.g., mary@example.com"
                    error={editParentForm.formState.errors.email?.message}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <PhoneNumberInput
                    value={editParentForm.watch('phone') || ''}
                    onChange={(phone) => editParentForm.setValue('phone', phone)}
                    placeholder="Enter phone number"
                    error={editParentForm.formState.errors.phone?.message}
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      setShowEditModal(false)
                      setEditingParent(null)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-purple-600 hover:bg-purple-700"
                    disabled={editParentForm.formState.isSubmitting}
                  >
                    {editParentForm.formState.isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircleIcon className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CSV Preview */}
      <AnimatePresence>
        {showPreview && csvData.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Data Preview ({csvData.length} records)
                </h3>
                <div className="flex space-x-2">
                  <button onClick={resetUpload} className="btn btn-secondary">Clear</button>
                  <button
                    onClick={handleBulkUpload}
                    disabled={isUploading}
                    className="btn btn-primary"
                  >
                    {isUploading ? (
                      <>
                        <ArrowDownTrayIcon className="h-4 w-4 mr-2 animate-bounce" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                        Start Upload
                      </>
                    )}
                  </button>
                </div>
              </div>

              {isUploading && (
                <div className="px-6 py-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Upload Progress</span>
                    <span>{Math.round(uploadProgress)}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="p-6 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Student Email</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Parent</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Parent Email</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {csvData.slice(0, 10).map((row, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm">
                          {(row.studentFirstName || row.firstName) && (row.studentLastName || row.lastName)
                            ? `${row.studentFirstName || row.firstName} ${row.studentLastName || row.lastName}`
                            : '-'}
                        </td>
                        <td className="px-3 py-2 text-sm">{row.studentEmail || row.email || '-'}</td>
                        <td className="px-3 py-2 text-sm">{row.studentGrade || row.grade || '-'}</td>
                        <td className="px-3 py-2 text-sm">
                          {row.parentFirstName && row.parentLastName
                            ? `${row.parentFirstName} ${row.parentLastName}`
                            : '-'}
                        </td>
                        <td className="px-3 py-2 text-sm">{row.parentEmail || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {csvData.length > 10 && (
                  <div className="px-3 py-2 text-xs text-gray-500 text-center bg-gray-50">
                    Showing first 10 records. {csvData.length - 10} more records will be uploaded.
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upload Results */}
      <AnimatePresence>
        {uploadResult && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Upload Results</h3>
                <button onClick={resetUpload} className="btn btn-secondary">Close</button>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                      <span className="text-sm font-medium text-green-900">Successful</span>
                    </div>
                    <p className="text-2xl font-bold text-green-900 mt-1">{uploadResult.success}</p>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                      <span className="text-sm font-medium text-red-900">Failed</span>
                    </div>
                    <p className="text-2xl font-bold text-red-900 mt-1">{uploadResult.failed}</p>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <UsersIcon className="h-5 w-5 text-blue-600 mr-2" />
                      <span className="text-sm font-medium text-blue-900">Total</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-900 mt-1">{uploadResult.total}</p>
                  </div>
                </div>

                {uploadResult.errors.length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Upload Errors</h4>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                      <div className="space-y-2">
                        {uploadResult.errors.map((error, index) => (
                          <div key={index} className="flex items-start space-x-2">
                            <ExclamationTriangleIcon className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm">
                              <span className="font-medium text-red-900">Row {error.row}:</span>
                              <span className="text-red-700 ml-1">{error.email}</span>
                              <p className="text-red-600 text-xs mt-1">{error.error}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CSV Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={closeUploadModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Upload CSV File</h3>
                    <p className="text-sm text-gray-500">Import students and parents from CSV</p>
                  </div>
                  <button onClick={closeUploadModal} className="text-gray-400 hover:text-gray-600">
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-blue-500 bg-blue-50'
                      : selectedFile
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {selectedFile ? (
                    <div className="space-y-3">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                        <CheckCircleIcon className="h-8 w-8 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                        <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <button onClick={() => setSelectedFile(null)} className="text-sm text-red-600 hover:text-red-800">
                        Remove file
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                        <CloudArrowUpIcon className={`h-8 w-8 ${isDragging ? 'text-blue-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {isDragging ? 'Drop your file here' : 'Drag and drop your CSV file'}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">or</p>
                      </div>
                      <label className="cursor-pointer">
                        <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden" />
                        <span className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                          Browse Files
                        </span>
                      </label>
                    </div>
                  )}
                </div>

                <div className="mt-4 text-xs text-gray-500 space-y-1">
                  <p>• CSV file with headers required</p>
                  <p>• Maximum 500 records per upload</p>
                  <p>• <button onClick={downloadTemplate} className="text-blue-600 hover:underline">Download template</button> for correct format</p>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <Button type="button" onClick={closeUploadModal} variant="secondary">Cancel</Button>
                <Button
                  type="button"
                  onClick={processSelectedFile}
                  disabled={!selectedFile}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  Process File
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Two-Step Form Wizard Modal */}
      <AnimatePresence>
        {showFormWizard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={resetWizard}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Add Family - Step {currentStep} of 2
                    </h3>
                    <p className="text-sm text-gray-500">
                      {currentStep === 1 ? 'Enter student details' : 'Enter parent details'}
                    </p>
                  </div>
                  <button onClick={resetWizard} className="text-gray-400 hover:text-gray-600">
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="flex items-center mt-4">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      currentStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                    }`}>1</div>
                    <span className="ml-2 text-sm font-medium text-gray-900">Student</span>
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="h-1 bg-gray-300 rounded-full">
                      <div className={`h-1 rounded-full transition-all ${
                        currentStep >= 2 ? 'bg-blue-600 w-full' : 'bg-gray-300 w-0'
                      }`}></div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      currentStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                    }`}>2</div>
                    <span className="ml-2 text-sm font-medium text-gray-900">Parent</span>
                  </div>
                </div>
              </div>

              {/* Step 1: Student Details */}
              {currentStep === 1 && (
                <form onSubmit={studentForm.handleSubmit(handleStudentFormNext)} className="p-6 space-y-4">
                  <div className="text-center mb-6">
                    <UserIcon className="mx-auto h-12 w-12 text-blue-600" />
                    <h4 className="mt-4 text-lg font-semibold text-gray-900">Student Information</h4>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input {...studentForm.register('firstName')} label="First Name *" placeholder="e.g., John" error={studentForm.formState.errors.firstName?.message} />
                    <Input {...studentForm.register('lastName')} label="Last Name *" placeholder="e.g., Doe" error={studentForm.formState.errors.lastName?.message} />
                    <Input {...studentForm.register('email')} type="email" label="Email *" placeholder="e.g., john@school.edu" error={studentForm.formState.errors.email?.message} />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone (Optional)</label>
                      <PhoneNumberInput
                        value={studentForm.watch('phone') || ''}
                        onChange={(phone) => studentForm.setValue('phone', phone)}
                        placeholder="Enter phone number"
                      />
                    </div>

                    {/* Grade Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Grade <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Search and select grade..."
                          value={gradeSearchQuery}
                          onChange={(e) => { setGradeSearchQuery(e.target.value); setShowGradeDropdown(true) }}
                          onFocus={() => setShowGradeDropdown(true)}
                          onBlur={() => setTimeout(() => setShowGradeDropdown(false), 150)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        {showGradeDropdown && grades.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {grades.filter(g => !gradeSearchQuery || g.name.toLowerCase().includes(gradeSearchQuery.toLowerCase())).map(grade => (
                              <button
                                key={grade.id}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); handleGradeSelection(grade) }}
                                className="w-full px-4 py-2 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium">{grade.name}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {studentForm.formState.errors.gradeId && <p className="text-red-500 text-sm mt-1">{studentForm.formState.errors.gradeId.message}</p>}
                    </div>

                    {/* Stream Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Stream <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <input
                          type="text"
                          placeholder={selectedGrade ? "Search and select stream..." : "Select grade first"}
                          value={streamSearchQuery}
                          onChange={(e) => { setStreamSearchQuery(e.target.value); setShowStreamDropdown(true) }}
                          onFocus={() => selectedGrade && setShowStreamDropdown(true)}
                          onBlur={() => setTimeout(() => setShowStreamDropdown(false), 150)}
                          disabled={!selectedGrade}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                        {showStreamDropdown && selectedGrade && streams.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {streams.filter(s => !streamSearchQuery || s.name.toLowerCase().includes(streamSearchQuery.toLowerCase())).map(stream => (
                              <button
                                key={stream.id}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); handleStreamSelection(stream) }}
                                className="w-full px-4 py-2 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium">{stream.name}</div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {studentForm.formState.errors.streamId && <p className="text-red-500 text-sm mt-1">{studentForm.formState.errors.streamId.message}</p>}
                    </div>

                    <Input {...studentForm.register('dateOfBirth')} type="date" label="Date of Birth *" max={new Date().toISOString().split('T')[0]} error={studentForm.formState.errors.dateOfBirth?.message} />
                    <Input {...studentForm.register('studentId')} label="Student ID (Optional)" placeholder="Leave empty for auto-generation" />
                  </div>

                  <div className="flex justify-end space-x-3 pt-6 border-t">
                    <Button type="button" onClick={resetWizard} variant="secondary">Cancel</Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                      Next: Parent Details
                      <ArrowRightIcon className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </form>
              )}

              {/* Step 2: Parent Details */}
              {currentStep === 2 && (
                <form onSubmit={parentWizardForm.handleSubmit(handleFormWizardSubmit)} className="p-6 space-y-4">
                  <div className="text-center mb-6">
                    <UserGroupIcon className="mx-auto h-12 w-12 text-purple-600" />
                    <h4 className="mt-4 text-lg font-semibold text-gray-900">Parent Information</h4>
                    <p className="text-sm text-gray-600">for {studentData?.firstName} {studentData?.lastName}</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                      <Input {...parentWizardForm.register('firstName')} placeholder="e.g., Mary" error={parentWizardForm.formState.errors.firstName?.message} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                      <Input {...parentWizardForm.register('lastName')} placeholder="e.g., Doe" error={parentWizardForm.formState.errors.lastName?.message} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                      <Input {...parentWizardForm.register('email')} type="email" placeholder="e.g., mary@example.com" error={parentWizardForm.formState.errors.email?.message} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone <span className="text-red-500">*</span></label>
                      <PhoneNumberInput
                        value={parentWizardForm.watch('phone') || ''}
                        onChange={(phone) => parentWizardForm.setValue('phone', phone)}
                        placeholder="Enter phone number"
                        error={parentWizardForm.formState.errors.phone?.message}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                      <select {...parentWizardForm.register('relationship')} className="form-input">
                        <option value="">Select Relationship</option>
                        <option value="Father">Father</option>
                        <option value="Mother">Mother</option>
                        <option value="Guardian">Guardian</option>
                        <option value="Grandfather">Grandfather</option>
                        <option value="Grandmother">Grandmother</option>
                        <option value="Uncle">Uncle</option>
                        <option value="Aunt">Aunt</option>
                      </select>
                    </div>
                    <Input {...parentWizardForm.register('occupation')} label="Occupation" placeholder="e.g., Teacher" />
                    <Input {...parentWizardForm.register('address')} label="Address" placeholder="e.g., 123 Main St" />
                    <Input {...parentWizardForm.register('emergencyContact')} label="Emergency Contact" placeholder="e.g., James Doe" />
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <h5 className="text-sm font-medium text-green-900">Ready to Create Family Accounts</h5>
                        <p className="text-sm text-green-700 mt-1">
                          Student: {studentData?.firstName} {studentData?.lastName} ({studentData?.email})
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between pt-6 border-t">
                    <Button type="button" onClick={() => setCurrentStep(1)} variant="secondary">
                      <ArrowLeftIcon className="h-4 w-4 mr-2" />
                      Back
                    </Button>
                    <Button type="submit" disabled={isSubmitting} className="bg-green-600 hover:bg-green-700">
                      {isSubmitting ? 'Creating...' : (
                        <>
                          <CheckCircleIcon className="h-4 w-4 mr-2" />
                          Enroll
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ParentsPage
